import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useChatContext } from '../providers/ChatProvider';

function parseWhatsAppFormatting(text) {
  if (typeof text !== 'string') return text;
  // 1. WhatsApp bold: *text* -> **text**
  let formatted = text.replace(/(?<!\*)\*(?!\s)([^*]+?)(?<!\s)\*(?!\*)/g, '**$1**');
  // 2. WhatsApp strikethrough: ~text~ -> ~~text~~
  formatted = formatted.replace(/(?<!~)~(?!\s)([^~]+?)(?<!\s)~(?!\s)/g, '~~$1~~');
  return formatted;
}

export default function ChatMessage({ msg, index, onSendSuggestion }) {
  const { theme, handleRetry, reply } = useChatContext();

  // ── SANITIZE: strip tool-call language from AI messages ──
  let displayContent = msg.content;
  if (msg.role !== 'user' && typeof msg.content === 'string') {
    displayContent = displayContent
      .replace(/Calling\s+\w+\s+to\s+[^.]+\.\s*/gi, '')
      .replace(/Calling\s+(get|update|replace|clear|remove|merge)_\w+[^.]*\.\s*/gi, '')
      .replace(/Score is now \d+\.\s*/gi, '')
      .replace(/Next:\s+add[^.]+\.\s*/gi, '')
      .replace(/<\|[^|>]+\|>/g, '')
      .replace(/functions\.[\w.]+:\d+/g, '')
      .replace(/\{"section":\s*"[^"]+"\}/g, '')
      .replace(/\{\}/g, '')
      .trim();
    if (!displayContent || displayContent.length < 3) {
      displayContent = '✅ Updated your resume!';
    }
  }

  displayContent = parseWhatsAppFormatting(displayContent);

  // ── Parse [SUGGEST: ...] into clickable chips ──
  let suggestions = [];
  if (msg.role !== 'user' && typeof displayContent === 'string') {
    const suggestMatch = displayContent.match(/\[SUGGEST:\s*([^\]]+)\]/i);
    if (suggestMatch) {
      suggestions = suggestMatch[1].split('|').map(s => s.trim()).filter(s => s.length > 0);
      displayContent = displayContent.replace(/\[SUGGEST:\s*[^\]]+\]/gi, '').trim();
    }
  }

  const isLastMessage = index === reply.length - 1;

  // ── HUMAN TYPING SIMULATION FOR WELCOME MESSAGE & LAST ASSISTANT MESSAGE ──
  const isAnimateTarget = (index === 0 && msg.role === 'assistant') || (isLastMessage && msg.role === 'assistant');
  const sessionKey = `welcome_typed_${msg.id || index}`;
  
  const [typingComplete, setTypingComplete] = React.useState(() => {
    if (!isAnimateTarget) return true;
    return !!sessionStorage.getItem(sessionKey);
  });
  
  const [typedText, setTypedText] = React.useState(() => {
    if (!isAnimateTarget || sessionStorage.getItem(sessionKey)) {
      return displayContent;
    }
    return '';
  });

  React.useEffect(() => {
    if (isAnimateTarget && !sessionStorage.getItem(sessionKey)) {
      let isCancelled = false;
      const text = displayContent;
      let currentIndex = 0;
      setTypedText('');

      const typeNextChar = () => {
        if (isCancelled) return;

        if (currentIndex >= text.length) {
          sessionStorage.setItem(sessionKey, 'true');
          setTypingComplete(true);
          window.dispatchEvent(new Event('welcome_typing_done'));
          return;
        }

        const char = text[currentIndex];

        // Typo simulation check: only on letters, not at the end of the text
        const canHaveTypo = /[a-zA-Z]/.test(char) && currentIndex < text.length - 5;
        const triggerTypo = canHaveTypo && Math.random() < 0.02; // 2% chance

        if (triggerTypo) {
          // Typo: pick a neighboring/random letter
          const wrongChars = "asdfghjklqwertyuiopzxcvbnm";
          const randomWrongChar = wrongChars[Math.floor(Math.random() * wrongChars.length)];
          
          // Type wrong character
          setTypedText(prev => prev + randomWrongChar);
          
          // Wait 180ms (simulating reaction time)
          setTimeout(() => {
            if (isCancelled) return;
            // Backspace: remove wrong character
            setTypedText(prev => prev.slice(0, -1));
            
            // Wait 150ms (finger repositioning), then type correct letter
            setTimeout(() => {
              if (isCancelled) return;
              setTypedText(prev => prev + char);
              currentIndex++;
              scheduleNext();
            }, 150);
          }, 180);
          
          return;
        }

        // Normal typing
        setTypedText(prev => prev + char);
        currentIndex++;
        scheduleNext();
      };

      const scheduleNext = () => {
        if (currentIndex >= text.length) {
          setTimeout(typeNextChar, 80);
          return;
        }

        const lastTypedChar = text[currentIndex - 1];
        let delay = Math.floor(Math.random() * 25) + 20; // Base delay: 20ms - 45ms (faster)

        // Humans naturally slow down or pause on punctuation and spaces
        if (['.', '!', '?'].includes(lastTypedChar)) {
          delay = Math.floor(Math.random() * 100) + 280; // 280ms - 380ms deep pause
        } else if (lastTypedChar === ',') {
          delay = Math.floor(Math.random() * 80) + 160; // 160ms - 240ms minor pause
        } else if (lastTypedChar === ' ') {
          delay = Math.floor(Math.random() * 25) + 35; // 35ms - 60ms space bar delay
        }

        setTimeout(typeNextChar, delay);
      };

      // Gentle entry delay before typing starts
      const startTimeout = setTimeout(typeNextChar, 300);

      return () => {
        isCancelled = true;
        clearTimeout(startTimeout);
      };
    }
  }, [isAnimateTarget, displayContent, sessionKey]);

  if (msg.role === 'system_confirmation') {
    return (
      <div className="flex justify-center my-3 animate-fade-in w-full">
        <div 
          className={`px-4 py-3 rounded-xl border text-[12px] max-w-[85%] ${
            theme === 'dark'
              ? 'bg-zinc-950/80 border-zinc-800/80 text-zinc-400'
              : 'bg-slate-50 border-slate-200/80 text-slate-500'
          }`}
          style={{ whiteSpace: 'pre-line', fontFamily: 'monospace' }}
        >
          {msg.content}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex animate-message ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
      <div className="max-w-[85%]">
        <div
          className={`px-4 py-3 text-[13px] leading-relaxed relative rounded-2xl ${msg.role === 'user'
            ? theme === 'dark'
              ? 'bg-white text-black rounded-br-md shadow-md font-medium'
              : 'bg-black text-white rounded-br-md shadow-md font-medium'
            : msg.isError
              ? 'bg-red-500/10 text-red-300 rounded-bl-md shadow-md border border-red-500/25'
              : theme === 'dark'
                ? 'bg-black text-white rounded-bl-md border border-white/20 shadow-md'
                : 'bg-white text-black rounded-bl-md border border-black/10 shadow-md'
            }`}
        >
          <div className="max-w-none text-[13px] leading-relaxed w-full">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
              p: ({ children }) => (
                <p className="mb-2 last:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                  {children}
                  {msg.role !== 'user' && !typingComplete && (
                    <span 
                      className="inline-block w-[1.5px] h-[12.5px] ml-1 bg-sky-500 animate-pulse rounded-sm align-middle" 
                      style={{ backgroundColor: 'var(--brand)' }} 
                    />
                  )}
                </p>
              ),
              h1: ({ children, ...hp }) => <h1 className="text-base font-bold my-2" {...hp}>{children}</h1>,
              h2: ({ children, ...hp }) => <h2 className="text-sm font-bold my-1.5" {...hp}>{children}</h2>,
              h3: ({ children, ...hp }) => <h3 className="text-[13px] font-bold my-1" {...hp}>{children}</h3>,
              ul: (up) => <ul className="list-disc pl-4 my-2 space-y-1" {...up} />,
              ol: (op) => <ol className="list-decimal pl-4 my-2 space-y-1" {...op} />,
              li: (lp) => <li className="my-0.5" style={{ whiteSpace: 'pre-wrap' }} {...lp} />,
              strong: (sp) => <strong className="font-bold" {...sp} />,
              blockquote: ({ children, ...bp }) => (
                <blockquote 
                  className={`border-l-4 pl-3 my-2.5 italic text-[12px] ${
                    theme === 'dark' ? 'border-white/25 text-white/60' : 'border-black/25 text-black/60'
                  }`} 
                  {...bp}
                >
                  {children}
                </blockquote>
              ),
              hr: (hp) => (
                <hr 
                  className={`my-3 border-t ${
                    theme === 'dark' ? 'border-white/10' : 'border-black/10'
                  }`} 
                  {...hp} 
                />
              ),
              table: (tp) => <table className="min-w-full border-collapse border border-white/20 my-2 text-left text-[11px]" {...tp} />,
              th: (tp) => <th className="border border-white/20 px-2.5 py-1.5 font-semibold bg-white/5" {...tp} />,
              td: (tp) => <td className="border border-white/20 px-2.5 py-1.5" {...tp} />,
              code: (cp) => cp.inline
                ? <code className="bg-white/10 rounded px-1 py-0.5 font-mono text-[11px]" {...cp} />
                : <code className="block bg-white/5 rounded-lg p-3 font-mono text-[11px] whitespace-pre-wrap my-2 border border-white/10" {...cp} />,
              // eslint-disable-next-line jsx-a11y/anchor-has-content
              a: (ap) => <a className="text-blue-400 hover:underline font-medium" target="_blank" rel="noopener noreferrer" {...ap} />,
            }}>
              {msg.role === 'user' ? displayContent : typedText}
            </ReactMarkdown>
          </div>
          {msg.isError && (
            <button
              onClick={() => handleRetry(index)}
              className="mt-2 flex items-center gap-1 text-red-400 hover:text-red-300 text-xs font-semibold transition-colors"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>
              Retry
            </button>
          )}
        </div>
        {suggestions.length > 0 && isLastMessage && (
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {suggestions.map((sug, si) => (
              <button
                key={si}
                onClick={() => onSendSuggestion(sug)}
                className={`px-3.5 py-2 rounded-xl text-[10.5px] font-semibold transition-all hover:scale-[1.04] active:scale-[0.96] shadow-sm hover:shadow-md ${theme === 'dark'
                  ? 'bg-black text-white border border-white/20 hover:bg-white hover:text-black hover:border-transparent'
                  : 'bg-white text-black border border-black/10 hover:bg-black hover:text-white hover:border-transparent'
                  }`}>
                {sug}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
